import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { InfoOutlined, WarningAmberRounded } from '@mui/icons-material';
import { useTenantStore } from '../../../stores/tenant.store';
import { formatDate } from '../../../lib/i18n-format';
import { programEnrollmentApi } from '../../../lib/api/program-enrollment.api';

// ─── Para una reunión de seguimiento ────────────────────────────────────────
//
// Lo que se decide en esa reunión no es «quién debe trabajo» sino CON QUIÉN HAY
// QUE HABLAR Y DE QUÉ. Por eso cada fila trae cuatro cosas que dicen cosas
// distintas, y una quinta que dice cuánto creerle a las otras.
//
// LO QUE EL ALUMNO DEBE Y LO QUE DEBEMOS NOSOTROS VAN EN COLUMNAS SEPARADAS y
// nunca se suman. Reclamarle a alguien por trabajo que SÍ entregó y no
// corregimos es el peor error posible ahí.
//
// LA DISPERSIÓN IMPORTA TANTO COMO EL TOTAL: cuatro faltantes en un curso es un
// problema con ese ramo; una en cada uno de cuatro es un problema de la persona.

export function StudentFollowUpTab({ termId }: { termId: string | null }): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [openStudent, setOpenStudent] = useState<string | null>(null);

  const reportQ = useQuery({
    queryKey: ['student-follow-up', currentTenantId, termId],
    enabled: !!currentTenantId && !!termId,
    queryFn: () =>
      programEnrollmentApi.getStudentFollowUp({ tenantId: currentTenantId!, termId: termId! }),
    staleTime: 60_000,
  });

  if (!termId) {
    return (
      <Typography color="text.secondary" sx={{ py: 4 }}>
        {t('reports.followUp.pickTerm')}
      </Typography>
    );
  }

  if (reportQ.isLoading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  const report = reportQ.data;
  if (!report || report.rows.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 4 }}>
        {t('reports.followUp.empty')}
      </Typography>
    );
  }

  return (
    <Stack spacing={3}>
      {/* LAS TRES CIFRAS QUE ABREN LA REUNIÓN. «Esperando corrección» va con su
          propio color a propósito: es lo único de acá que NO depende de los
          alumnos. */}
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Stack direction="row" spacing={3} alignItems="flex-start" flexWrap="wrap" useFlexGap>
          <Figure
            value={report.totals.studentsWithMissing}
            label={t('reports.followUp.studentsWithMissing')}
            color="error.main"
          />
          <Figure
            value={report.totals.missing}
            label={t('reports.followUp.totalMissing')}
            color="error.main"
          />
          <Figure
            value={report.totals.awaitingGrading}
            label={t('reports.followUp.totalAwaiting')}
            color="warning.main"
            help={t('reports.followUp.awaitingHelp')}
          />
          <Figure value={report.totals.late} label={t('reports.followUp.totalLate')} />
        </Stack>
      </Paper>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('reports.followUp.colStudent')}</TableCell>
              <TableCell align="right">{t('reports.followUp.colMissing')}</TableCell>
              <TableCell align="right">{t('reports.followUp.colCourses')}</TableCell>
              <TableCell align="right">{t('reports.followUp.colAwaiting')}</TableCell>
              <TableCell align="right">{t('reports.followUp.colLate')}</TableCell>
              <TableCell>{t('reports.followUp.colLastSubmitted')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* SIN ORDEN PROPIO: manda el que trae el informe —quien nunca
                entregó primero—. Es la lección de #503, donde un orden inicial
                del cliente descartó el del servidor y escondió el peor caso. */}
            {report.rows.map((row) => (
              <Fragment key={row.studentId}>
                <TableRow
                  hover
                  sx={{ cursor: row.breakdown.length ? 'pointer' : 'default' }}
                  onClick={() =>
                    row.breakdown.length &&
                    setOpenStudent(openStudent === row.studentId ? null : row.studentId)
                  }
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {row.studentName}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {row.missing > 0 ? (
                      <Chip size="small" color="error" label={row.missing} />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="right">{row.coursesAffected || '—'}</TableCell>
                  <TableCell align="right">
                    {row.awaitingGrading > 0 ? (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={row.awaitingGrading}
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="right">{row.late || '—'}</TableCell>
                  <TableCell>
                    {row.lastSubmittedAt ? (
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(row.lastSubmittedAt)}
                      </Typography>
                    ) : (
                      // NUNCA ENTREGÓ NADA es un caso distinto de «hace mucho»,
                      // y en producción son cinco alumnos. Una fecha vacía lo
                      // escondería.
                      <Chip
                        size="small"
                        color="error"
                        variant="outlined"
                        label={t('reports.followUp.never')}
                      />
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                    <Collapse in={openStudent === row.studentId} unmountOnExit>
                      <Box sx={{ py: 1.5, pl: 2 }}>
                        <Stack spacing={1}>
                          {row.breakdown.map((b) => (
                            <Stack
                              key={b.sectionId}
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Typography variant="body2" sx={{ minWidth: 180 }}>
                                {b.courseName}
                                {b.sectionCode ? ` · ${b.sectionCode}` : ''}
                              </Typography>
                              {b.missing > 0 && (
                                <Typography variant="caption" color="error.main">
                                  {t('reports.followUp.breakdownMissing', { count: b.missing })}
                                </Typography>
                              )}
                              {b.awaitingGrading > 0 && (
                                <Typography variant="caption" color="warning.main">
                                  {t('reports.followUp.breakdownAwaiting', {
                                    count: b.awaitingGrading,
                                  })}
                                </Typography>
                              )}
                              {b.late > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  {t('reports.followUp.breakdownLate', { count: b.late })}
                                </Typography>
                              )}
                              {/* LA ADVERTENCIA QUE PIDIÓ EL DIRECTOR: con la
                                  cobertura del curso por debajo del 100 %, «no
                                  entregó» puede ser «entregó en papel y no se
                                  registró». No se bloquea el dato; se dice
                                  cuánto creerle. */}
                              {b.missing > 0 &&
                                b.sectionCoverage !== null &&
                                b.sectionCoverage < 1 && (
                                  <Tooltip title={t('reports.followUp.coverageWarningHelp')}>
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      color="warning"
                                      icon={<WarningAmberRounded />}
                                      label={t('reports.followUp.coverageWarning', {
                                        pct: Math.round(b.sectionCoverage * 100),
                                      })}
                                    />
                                  </Tooltip>
                                )}
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function Figure({
  value,
  label,
  color,
  help,
}: {
  value: number;
  label: string;
  color?: string;
  help?: string;
}): JSX.Element {
  return (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Typography
          variant="h4"
          fontWeight={800}
          lineHeight={1}
          sx={{ whiteSpace: 'nowrap', ...(color ? { color } : {}) }}
        >
          {value}
        </Typography>
        {help && (
          <Tooltip title={help}>
            <InfoOutlined sx={{ fontSize: 16, color: 'text.disabled' }} />
          </Tooltip>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
    </Box>
  );
}
